// MKS-229 — which months an "assign person" click writes, and with how much FTE.
//
// The capacity grid's assign modal can fan one person out over every following
// month that still needs the same role. It used to send the FTE typed for the
// first month to all of them, so needs of 0,5 / 0,3 / 0,1 all became 0,5. Each
// subsequent month now gets that month's OWN remaining gap, capped at what the
// user typed: the dialog allocates one person's FTE, so it never raises the
// number above the typed value, only lowers it to what the month still needs.
//
// The start month keeps the typed value untouched — over-allocating that one
// cell is an explicit choice the user made in the input.

export interface CapacityNeedLike {
  roleId: number;
  month: string;
  fte: number;
}

export interface CapacityAssignmentLike {
  userId: number;
  roleId: number;
  month: string;
  fte: number;
}

export interface PlanCapacityAssignmentsInput {
  roleId: number;
  userId: number;
  /** Month the modal was opened from (YYYY-MM). */
  startMonth: string;
  /** FTE the user typed, already parsed. */
  fte: number;
  /** Visible month keys, ascending (YYYY-MM). */
  months: string[];
  needs: CapacityNeedLike[];
  assignments: CapacityAssignmentLike[];
  /** "Also assign to all subsequent months with same role need" checkbox. */
  assignSubsequent: boolean;
  /** Project date-range clamp (KYI-CAP-02); months outside it are dropped. */
  isMonthAllowed?: (month: string) => boolean;
}

export interface PlannedCapacityAssignment {
  month: string;
  fte: number;
}

// DECIMAL(4,2) on the backend: anything with more precision is rejected by
// assertFteTwoDecimals, and anything below 0.01 by capacityFteJsonSchema.
const MIN_FTE = 0.01;

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export function planCapacityAssignments({
  roleId,
  userId,
  startMonth,
  fte,
  months,
  needs,
  assignments,
  assignSubsequent,
  isMonthAllowed,
}: PlanCapacityAssignmentsInput): PlannedCapacityAssignment[] {
  const allowed = isMonthAllowed ?? (() => true);

  const targetMonths = assignSubsequent
    ? months.filter(
        (m) =>
          m >= startMonth &&
          allowed(m) &&
          needs.some((n) => n.roleId === roleId && n.month === m)
      )
    : [startMonth];

  const planned: PlannedCapacityAssignment[] = [];

  for (const month of targetMonths) {
    const alreadyAssigned = assignments.some(
      (a) => a.userId === userId && a.roleId === roleId && a.month === month
    );
    if (alreadyAssigned) continue;

    const need = needs.find((n) => n.roleId === roleId && n.month === month);
    // Whole cents, not floats: 0.3 - (0.1 + 0.1) is 0.09999999999999998 and
    // 0.07 - 0.06 is 0.009999999999999995, which a naive `< 0.01` guard would
    // drop as an empty gap. Every stored FTE is DECIMAL(4,2), so cents are exact.
    const needCents = need ? Math.round(need.fte * 100) : 0;
    const assignedCents = assignments
      .filter((a) => a.roleId === roleId && a.month === month)
      .reduce((s, a) => s + Math.round(a.fte * 100), 0);
    if (need && assignedCents >= needCents) continue;

    if (month === startMonth) {
      // The typed value wins for the cell the user opened.
      planned.push({ month, fte: round2(fte) });
      continue;
    }

    // No need row cannot happen here (targetMonths filtered on it), but keep
    // the fallback honest rather than asserting.
    const gap = need ? (needCents - assignedCents) / 100 : round2(fte);
    const monthFte = round2(Math.min(fte, gap));
    if (monthFte < MIN_FTE) continue;

    planned.push({ month, fte: monthFte });
  }

  return planned;
}
